import React, { Component } from 'react';
import PropTypes from 'prop-types';
import * as ladda from 'ladda/js/ladda.js';

// Constants for compatibility with react-ladda
export const EXPAND_LEFT = 'expand-left';
export const EXPAND_RIGHT = 'expand-right';
export const EXPAND_UP = 'expand-up';
export const EXPAND_DOWN = 'expand-down';
export const CONTRACT = 'contract';
export const CONTRACT_OVERLAY = 'contract-overlay';
export const ZOOM_IN = 'zoom-in';
export const ZOOM_OUT = 'zoom-out';
export const SLIDE_LEFT = 'slide-left';
export const SLIDE_RIGHT = 'slide-right';
export const SLIDE_UP = 'slide-up';
export const SLIDE_DOWN = 'slide-down';

export const XS = 'xs';
export const S = 's';
export const L = 'l';
export const XL = 'xl';

const STYLES = [
  EXPAND_LEFT, EXPAND_RIGHT, EXPAND_UP, EXPAND_DOWN,
  CONTRACT, CONTRACT_OVERLAY, ZOOM_IN, ZOOM_OUT,
  SLIDE_LEFT, SLIDE_RIGHT, SLIDE_UP, SLIDE_DOWN
];

const SIZES = [XS, S, L, XL];

const isUndefined = value => typeof value === 'undefined';

const OMITTED_PROPS = ['loading', 'progress'];

const omit = (data, keys) => {
  const result = {};
  Object.keys(data).forEach(key => {
    if (keys.indexOf(key) === -1) {
      result[key] = data[key];
    }
  });
  return result;
};

class LaddaButton extends Component {
  static propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    progress: PropTypes.number,
    loading: PropTypes.bool,
    disabled: PropTypes.bool,
    // Ladda props
    'data-color': PropTypes.oneOf(['green', 'red', 'blue', 'purple', 'mint']),
    'data-size': PropTypes.oneOf(SIZES),
    'data-style': PropTypes.oneOf(STYLES),
    'data-spinner-size': PropTypes.number,
    'data-spinner-color': PropTypes.string,
    'data-spinner-lines': PropTypes.number
  };

  componentDidMount() {
    try {
      // Try to create ladda instance with proper error handling
      if (ladda && ladda.create && this.node) {
        this.laddaInstance = ladda.create(this.node);
        if (this.props.loading) {
          this.laddaInstance.start();
        }
      }
    } catch (error) {
      console.warn('Ladda initialization failed:', error);
      // Fallback: just manage loading state with CSS classes
      this.fallbackMode = true;
      if (this.props.loading && this.node) {
        this.node.classList.add('ladda-button', 'loading');
      }
    }
  }

  componentDidUpdate(prevProps) {
    if (this.laddaInstance) {
      if (!isUndefined(this.props.progress)) {
        this.laddaInstance.setProgress(this.props.progress);
      }

      if (prevProps.loading !== this.props.loading) {
        if (this.props.loading) {
          this.laddaInstance.start();
        } else {
          this.laddaInstance.stop();
        }
      }
    } else if (this.fallbackMode && this.node) {
      // Fallback mode: manage CSS classes manually
      if (prevProps.loading !== this.props.loading) {
        if (this.props.loading) {
          this.node.classList.add('loading');
        } else {
          this.node.classList.remove('loading');
        }
      }
    }
  }

  componentWillUnmount() {
    if (this.laddaInstance) {
      this.laddaInstance.remove();
    }
  }

  render() {
    const { children, loading, progress, disabled, className, ...rest } = this.props;
    
    // Add ladda-button class and other necessary classes
    const buttonClasses = [
      'ladda-button',
      className || '',
      loading ? 'loading' : ''
    ].filter(Boolean).join(' ');

    return (
      <button
        {...omit(rest, OMITTED_PROPS)}
        ref={node => { this.node = node; }}
        className={buttonClasses}
        disabled={disabled || loading}
      >
        <span className="ladda-label">{children}</span>
        <span className="ladda-spinner"></span>
      </button>
    );
  }
}

export default LaddaButton;